import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  buildPlanV2IssueKey,
  PlanV2IssueCardPayload,
  PlanV2IssueDispositionRecord,
  PlanV2IssueQueue,
  PlanV2LivingBriefPeriod,
  PlanV2RegenerateScope,
  PlanV2WorkItemsUrlGroup,
  resolvePlanV2IssueQueue
} from '../utils/plan-v2-issues';
import type { ActionPlanInternalLinkRow } from './inventory-plan-clusters.service';
import type { SupportingPagesV2RowResult } from './dataforseo';
import {
  buildInternalLinkRowLookup,
  collectFocalUrlsWithSectionGapWorkItems,
  collectFreshnessWorkItemUrls,
  repairFreshnessWorkItemRecord,
  repairSupportingSectionWorkItemRecord,
  splitInternalLinkWorkItemForRepair,
  type FreshnessWorkItemContext
} from '../utils/plan-v2-work-item-compose';
import {
  groupWorkItemsForDisplay,
  urlWorkItemGroups,
  type PlanV2WorkItemGroup
} from '../utils/plan-v2-work-item-groups';
import {
  planPlanV2UrlReopen,
  type PlanV2UrlReopenPlan
} from '../utils/plan-v2-url-reopen';
import { decidedUrlIdsFrom } from '../utils/review-decided-urls';

interface PlanV2IssuesListResponse {
  success: boolean;
  data: {
    domain: string;
    /** This period's living brief only. Last period's rows arrive separately —
     *  mixing them back in is what the LB3 chapter turn exists to stop. */
    items: PlanV2IssueDispositionRecord[];
    previousItems?: PlanV2IssueDispositionRecord[];
    period?: PlanV2LivingBriefPeriod | null;
  };
}

interface PlanV2IssuePatchResponse {
  success: boolean;
  data: {
    domain: string;
    issueKey: string;
    queue: PlanV2IssueQueue;
    item: PlanV2IssueDispositionRecord | null;
  };
}

interface PlanV2RegenerateResponse {
  success: boolean;
  data: {
    domain: string;
    scope?: PlanV2RegenerateScope;
    clearedCount: number;
    keptCount?: number;
  };
}

/** Mirrors the batch endpoint's own cap (`backend/src/routes/urls.ts`). */
const PLAN_V2_ISSUE_BATCH_LIMIT = 100;

@Injectable({ providedIn: 'root' })
export class PlanV2IssueQueueService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly recordsSubject = new BehaviorSubject<Map<string, PlanV2IssueDispositionRecord>>(new Map());
  /** Last period's frozen rows. Read-only apart from Carry forward. */
  private readonly previousRecordsSubject = new BehaviorSubject<PlanV2IssueDispositionRecord[]>([]);
  private readonly periodSubject = new BehaviorSubject<PlanV2LivingBriefPeriod | null>(null);
  private readonly exitingSubject = new BehaviorSubject<{ issueKey: string; kind: 'work_item' | 'dismissed' } | null>(
    null
  );
  /** Matches `plan-v2-card-exit-*` CSS duration so the card stays mounted through the animation. */
  private readonly exitAnimationMs = 240;
  private exitStartedAt = 0;
  private activeDomain = '';

  readonly records$ = this.recordsSubject.asObservable();
  readonly previousRecords$ = this.previousRecordsSubject.asObservable();
  readonly period$ = this.periodSubject.asObservable();
  readonly exiting$ = this.exitingSubject.asObservable();

  get records(): Map<string, PlanV2IssueDispositionRecord> {
    return this.recordsSubject.value;
  }

  /** Last period's items, newest work first. Compare only until carried forward. */
  get previousRecords(): PlanV2IssueDispositionRecord[] {
    return this.previousRecordsSubject.value;
  }

  get period(): PlanV2LivingBriefPeriod | null {
    return this.periodSubject.value;
  }

  get hasPreviousPeriod(): boolean {
    return this.previousRecords.length > 0;
  }

  get activeScopeDomain(): string {
    return this.activeDomain;
  }

  constructor(private readonly http: HttpClient) {}

  load(domain: string): Observable<PlanV2IssuesListResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    this.activeDomain = scopeDomain;
    const params = new URLSearchParams({ domain: scopeDomain });
    return this.http
      .get<PlanV2IssuesListResponse>(`${this.API_URL}/urls/action-plan/v2/issues?${params}`)
      .pipe(
        tap((res) => {
          const map = new Map<string, PlanV2IssueDispositionRecord>();
          for (const item of res?.data?.items ?? []) {
            map.set(item.issueKey, item);
          }
          this.recordsSubject.next(map);
          this.previousRecordsSubject.next(res?.data?.previousItems ?? []);
          this.periodSubject.next(res?.data?.period ?? null);
        })
      );
  }

  /** Last period's accepted work, for the Previous list. */
  previousWorkItems(): PlanV2IssueDispositionRecord[] {
    return this.previousRecords
      .filter((r) => r.queue === 'work_item')
      .sort((a, b) => (b.priorityScore ?? 0) - (a.priorityScore ?? 0) || a.cardTitle.localeCompare(b.cardTitle));
  }

  previousWorkItemGroups(): PlanV2WorkItemGroup[] {
    return this.buildWorkItemGroups(this.previousWorkItems());
  }

  previousWorkItemsByUrl(): PlanV2WorkItemsUrlGroup[] {
    return urlWorkItemGroups(this.previousWorkItemGroups());
  }

  previousDismissedItems(): PlanV2IssueDispositionRecord[] {
    return this.previousRecords
      .filter((r) => r.queue === 'dismissed')
      .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }

  previousItemCount(): number {
    return this.previousRecords.length;
  }

  /**
   * "Keep working — add to this period." The Previous row stays put and is
   * stamped as carried forward; a fresh row lands in this period's brief.
   */
  carryForward(domain: string, issueKey: string): Observable<PlanV2IssuePatchResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    return this.http
      .post<PlanV2IssuePatchResponse>(
        `${this.API_URL}/urls/action-plan/v2/issues/${encodeURIComponent(issueKey)}/carry-forward`,
        { domain: scopeDomain }
      )
      .pipe(
        tap((res) => {
          const item = res?.data?.item;
          if (!item) return;
          const next = new Map(this.records);
          next.set(item.issueKey, item);
          this.recordsSubject.next(next);
          this.previousRecordsSubject.next(
            this.previousRecords.map((row) =>
              row.issueKey === item.issueKey ? { ...row, carriedForwardAt: new Date().toISOString() } : row
            )
          );
        })
      );
  }

  getQueue(issueKey: string): PlanV2IssueQueue {
    return resolvePlanV2IssueQueue(issueKey, this.records);
  }

  isVisibleInIssuesEmbed(issueKey: string): boolean {
    return this.getQueue(issueKey) === 'issues';
  }

  /**
   * The work-items list: a Site-wide group, then a group per page.
   *
   * The rule lives in `utils/plan-v2-work-item-groups` — a site-wide defect
   * stores the page it was decided on, and grouping on that alone filed one
   * template fix under one arbitrary URL.
   */
  private buildWorkItemGroups(items: PlanV2IssueDispositionRecord[]): PlanV2WorkItemGroup[] {
    return groupWorkItemsForDisplay(items);
  }

  /** This period's commitments — the living brief half of the split. */
  workItems(): PlanV2IssueDispositionRecord[] {
    return [...this.records.values()]
      .filter((r) => r.queue === 'work_item')
      .sort((a, b) => (b.priorityScore ?? 0) - (a.priorityScore ?? 0) || a.cardTitle.localeCompare(b.cardTitle));
  }

  workItemGroups(): PlanV2WorkItemGroup[] {
    return this.buildWorkItemGroups(this.workItems());
  }

  /** Per-page groups only — what "N pages" counts. */
  workItemsByUrl(): PlanV2WorkItemsUrlGroup[] {
    return urlWorkItemGroups(this.workItemGroups());
  }

  /**
   * Pages whose only live decision is a dismissed card.
   *
   * These fall through every existing list. They are not work items, so the
   * "Re-open page" button never offers them; they are not `url_ignored`, so
   * "Show ignored" never lists them. Before the belt stopped treating a
   * dismissal as a whole-page decision they were also invisible IN the queue —
   * a page could leave review because one card was waved off, and the only
   * trace was the Dashboard strip's page count disagreeing with its own button.
   *
   * The page comes back on its own now. Its dismissed CARDS do not, and this is
   * what makes them findable.
   */
  dismissedOnlyUrlGroups(): PlanV2WorkItemsUrlGroup[] {
    const byUrl = new Map<string, PlanV2IssueDispositionRecord[]>();
    const decided = new Set<string>();

    for (const record of this.records.values()) {
      const urlId = String(record?.urlId || '').trim();
      if (!urlId) continue;
      // Check-scoped rows are a period preference, not a card decision, and
      // restoring them here would silently un-mute site-wide work.
      if (record.issueType === 'check_muted' || record.issueType === 'site_wide_technical') continue;
      if (record.queue === 'work_item' || record.issueType === 'url_ignored') {
        decided.add(urlId);
        continue;
      }
      if (record.queue !== 'dismissed') continue;
      const bucket = byUrl.get(urlId);
      if (bucket) bucket.push(record);
      else byUrl.set(urlId, [record]);
    }

    const out: PlanV2WorkItemsUrlGroup[] = [];
    for (const [urlId, items] of byUrl) {
      if (decided.has(urlId)) continue;
      out.push({
        urlId,
        url: items[0]?.url || '',
        priorityScore: Math.max(...items.map((i) => i.priorityScore ?? 0), 0),
        items
      });
    }
    return out.sort((a, b) => b.priorityScore - a.priorityScore || a.url.localeCompare(b.url));
  }

  /** Dismissed cards and ignored URLs in this period's triage. */
  dismissedCount(): number {
    return [...this.records.values()].filter((r) => r.queue === 'dismissed').length;
  }

  /** Rows a current-period regenerate would clear. */
  currentPeriodCount(): number {
    return this.records.size;
  }

  urlIgnoredIssueKey(urlId: string): string {
    return buildPlanV2IssueKey(urlId.trim(), 'url_ignored');
  }

  isUrlIgnored(urlId: string): boolean {
    const hit = this.records.get(this.urlIgnoredIssueKey(urlId));
    return hit?.issueType === 'url_ignored' && hit.queue === 'dismissed';
  }

  ignoredUrlIds(): Set<string> {
    const ids = new Set<string>();
    for (const rec of this.records.values()) {
      if (rec.issueType === 'url_ignored' && rec.queue === 'dismissed') {
        ids.add(rec.urlId);
      }
    }
    return ids;
  }

  /**
   * Pages the operator has already dealt with this period — the belt's other
   * skip list, and the bigger one.
   *
   * `ignoredUrlIds()` above covers the pages someone explicitly Put aside. This
   * covers the pages they actually *worked*: every card sent to work items,
   * every card dismissed, whatever the mix. Without it a page whose cards were
   * all sent stayed in the belt as an empty screen with no Send button on it,
   * and since the cards differ per page the ones that stalled read as arbitrary.
   *
   * It subsumes `ignoredUrlIds()` — `url_ignored` is a page-scoped disposition
   * like any other — and callers still pass both, so the skip list stays
   * readable at the call site rather than depending on that fact.
   *
   * The rule itself lives in `utils/review-decided-urls`, which is where it is
   * specced and where the two check-scoped exceptions are argued.
   */
  decidedUrlIds(): Set<string> {
    return decidedUrlIdsFrom(this.records.values());
  }

  ignoreUrl(
    domain: string,
    urlId: string,
    url: string,
    priorityScore?: number | null
  ): Observable<PlanV2IssuePatchResponse> {
    const payload: PlanV2IssueCardPayload = {
      issueKey: this.urlIgnoredIssueKey(urlId),
      urlId: urlId.trim(),
      url: url.trim(),
      issueType: 'url_ignored',
      cardTitle: 'URL ignored',
      changeSummary: 'Hidden from this period’s brief until you restore it or regenerate the queue.',
      why: 'Ignored by reviewer.',
      priorityScore: priorityScore ?? null
    };
    return this.setDisposition(domain, payload.issueKey, 'dismissed', payload);
  }

  restoreIgnoredUrl(domain: string, urlId: string): Observable<PlanV2IssuePatchResponse> {
    return this.setDisposition(domain, this.urlIgnoredIssueKey(urlId), 'issues');
  }

  /** What a re-open of this page would move, and what it would leave. */
  urlReopenPlan(urlId: string, url: string): PlanV2UrlReopenPlan {
    return planPlanV2UrlReopen(urlId, url, this.records.values());
  }

  /**
   * Put one page back into the belt.
   *
   * One request, because it is one decision — the same reason review mode
   * sends a page's triage in a batch rather than as N patches. A half-applied
   * re-open would leave the page in a state the operator never chose.
   *
   * Site-wide and muted-check rows are excluded by the plan, not here: what
   * belongs to the page is a rule worth testing on its own.
   */
  reopenUrl(domain: string, plan: PlanV2UrlReopenPlan): Observable<number> {
    if (!plan.issueKeys.length) return of(0);
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    // The endpoint refuses more than 100 entries. A page never has that many,
    // but "never" is not a reason to send a request that would 400 if it did.
    const chunks: string[][] = [];
    for (let i = 0; i < plan.issueKeys.length; i += PLAN_V2_ISSUE_BATCH_LIMIT) {
      chunks.push(plan.issueKeys.slice(i, i + PLAN_V2_ISSUE_BATCH_LIMIT));
    }
    return forkJoin(
      chunks.map((keys) =>
        this.http.post<{ success: boolean }>(`${this.API_URL}/urls/action-plan/v2/issues/batch`, {
          domain: scopeDomain,
          items: keys.map((issueKey) => ({ issueKey, queue: 'issues' }))
        })
      )
    )
      .pipe(
        tap(() => {
          // `issues` means no row, so the local map loses the keys the same way
          // a single Back-to-issues does. No reload needed to see the page
          // return to the queue.
          const next = new Map(this.records);
          for (const issueKey of plan.issueKeys) next.delete(issueKey);
          this.recordsSubject.next(next);
        }),
        map(() => plan.issueKeys.length)
      );
  }

  /**
   * Reset triage. Defaults to this period only — Previous is history the
   * operator compares against, not queue state (LIVING_BRIEF_PLAN.md §1).
   */
  regenerateQueue(domain: string, scope: PlanV2RegenerateScope = 'current'): Observable<PlanV2RegenerateResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    return this.http
      .post<PlanV2RegenerateResponse>(`${this.API_URL}/urls/action-plan/v2/issues/regenerate`, {
        domain: scopeDomain,
        scope
      })
      .pipe(
        tap(() => {
          if (scope === 'all') {
            this.recordsSubject.next(new Map());
            this.previousRecordsSubject.next([]);
            return;
          }
          if (scope === 'current') {
            this.recordsSubject.next(new Map());
            return;
          }
          this.previousRecordsSubject.next([]);
        })
      );
  }

  beginCardExit(issueKey: string, kind: 'work_item' | 'dismissed'): void {
    this.exitStartedAt = Date.now();
    this.exitingSubject.next({ issueKey, kind });
  }

  clearCardExit(immediate = false): void {
    if (immediate) {
      this.exitingSubject.next(null);
      return;
    }
    const remaining = this.exitAnimationMs - (Date.now() - this.exitStartedAt);
    if (remaining <= 0) {
      this.exitingSubject.next(null);
    } else {
      window.setTimeout(() => this.exitingSubject.next(null), remaining);
    }
  }

  isCardExiting(issueKey: string, kind: 'work_item' | 'dismissed'): boolean {
    const ex = this.exitingSubject.value;
    return Boolean(ex && ex.issueKey === issueKey && ex.kind === kind);
  }

  setDisposition(
    domain: string,
    issueKey: string,
    queue: PlanV2IssueQueue,
    payload?: PlanV2IssueCardPayload
  ): Observable<PlanV2IssuePatchResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    return this.http
      .patch<PlanV2IssuePatchResponse>(
        `${this.API_URL}/urls/action-plan/v2/issues/${encodeURIComponent(issueKey)}`,
        {
          domain: scopeDomain,
          queue,
          ...(payload ? { payload } : {})
        }
      )
      .pipe(
        tap((res) => {
          const next = new Map(this.records);
          const item = res?.data?.item;
          if (queue === 'issues' || !item) {
            next.delete(issueKey);
          } else {
            // The PATCH response does not recompute an LB4 history state — that
            // is a read-time comparison against the crawl pair, not something a
            // save changes. Carry the one we already have so the chip does not
            // blink out on every edit; the next list load refreshes it.
            const previous = this.records.get(issueKey);
            next.set(
              issueKey,
              item.historyState === undefined && previous
                ? {
                    ...item,
                    historyState: previous.historyState ?? null,
                    historyStateLabel: previous.historyStateLabel ?? null,
                    historyStateReason: previous.historyStateReason ?? null,
                    historyStateRule: previous.historyStateRule ?? null
                  }
                : item
            );
          }
          this.recordsSubject.next(next);
          this.clearCardExit();
        })
      );
  }

  /** Remove the Issues-card row and persist one work item per selected internal link. */
  addInternalLinkWorkItems(
    domain: string,
    issuesCardIssueKey: string,
    payloads: PlanV2IssueCardPayload[]
  ): Observable<number> {
    const valid = payloads.filter((p) => {
      const detail = (p.actionDetail || '').trim();
      return detail && !detail.includes('Re-open the issue and pick links');
    });
    if (!valid.length) {
      return of(0);
    }
    return this.setDisposition(domain, issuesCardIssueKey, 'issues').pipe(
      switchMap(() =>
        forkJoin(
          valid.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
        ).pipe(map((results) => results.length))
      )
    );
  }

  /**
   * Client-side bulk repairs for persisted work items (see docs/TECHNICAL_DEBT.md §0h).
   * Remove once actionDetail is versioned/migrated server-side.
   */
  /** Re-home batched internal-link tasks from beneficiary URLs onto source pages. */
  repairMisplacedInternalLinkWorkItems(
    domain: string,
    linkRows: ActionPlanInternalLinkRow[]
  ): Observable<number> {
    const lookup = buildInternalLinkRowLookup(linkRows);
    const repairs: Array<{ deleteIssueKey: string; replacements: PlanV2IssueCardPayload[] }> = [];
    for (const record of this.records.values()) {
      const split = splitInternalLinkWorkItemForRepair(record, lookup);
      if (split) repairs.push(split);
    }
    if (!repairs.length) {
      return of(0);
    }

    const ops: Observable<PlanV2IssuePatchResponse>[] = [];
    for (const repair of repairs) {
      ops.push(this.setDisposition(domain, repair.deleteIssueKey, 'issues'));
      for (const payload of repair.replacements) {
        ops.push(this.setDisposition(domain, payload.issueKey, 'work_item', payload));
      }
    }
    return forkJoin(ops).pipe(
      map(() => repairs.reduce((count, repair) => count + repair.replacements.length, 0))
    );
  }

  /** Re-label saved section gaps using supporting-pages v2 snapshot paths. */
  repairSupportingSectionWorkItems(
    domain: string,
    v2ByFocalUrl: Map<string, SupportingPagesV2RowResult[]>
  ): Observable<number> {
    const payloads: PlanV2IssueCardPayload[] = [];
    for (const record of this.records.values()) {
      const payload = repairSupportingSectionWorkItemRecord(record, v2ByFocalUrl);
      if (payload) payloads.push(payload);
    }
    if (!payloads.length) {
      return of(0);
    }
    return forkJoin(
      payloads.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
    ).pipe(map((results) => results.length));
  }

  /** Recompose saved freshness cards when SERP band / copy rules change. */
  repairFreshnessWorkItems(
    domain: string,
    contextByUrlId: Map<string, FreshnessWorkItemContext>
  ): Observable<number> {
    const payloads: PlanV2IssueCardPayload[] = [];
    for (const record of this.records.values()) {
      const payload = repairFreshnessWorkItemRecord(record, contextByUrlId);
      if (payload) payloads.push(payload);
    }
    if (!payloads.length) {
      return of(0);
    }
    return forkJoin(
      payloads.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
    ).pipe(map((results) => results.length));
  }

  focalUrlsNeedingSectionGapRepair(): string[] {
    return collectFocalUrlsWithSectionGapWorkItems(this.records);
  }

  freshnessWorkItemUrls(): Array<{ urlId: string; url: string }> {
    return collectFreshnessWorkItemUrls(this.records);
  }
}
